import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Context interface for tracking component viewport visibility
 */
interface PositionMarkerContextValue {
  /** Whether the tracked component is currently in view */
  isComponentInView: boolean;
  /** Callback to update the component's visibility state */
  setComponentOutOfView: (isInView: boolean) => void;
}

/**
 * Context for managing position marker state
 */
const PositionMarkerContext = createContext<PositionMarkerContextValue | undefined>(undefined);

/**
 * Props for the PositionMarkerProvider component
 */
interface PositionMarkerProviderProps {
  /** Child components to render */
  children: ReactNode;
}

/**
 * Provider component for position marker context
 * Manages the visibility state of a single tracked component
 */
export function PositionMarkerProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const [isComponentInView, setIsComponentInView] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isInView => {
      setIsComponentInView(isInView);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setComponentOutOfView = t1;
  let t2;
  if ($[1] !== isComponentInView) {
    t2 = {
      isComponentInView,
      setComponentOutOfView
    };
    $[1] = isComponentInView;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const value = t2;
  let t3;
  if ($[3] !== children || $[4] !== value) {
    t3 = <PositionMarkerContext.Provider value={value}>{children}</PositionMarkerContext.Provider>;
    $[3] = children;
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Hook to access position marker context
 * @throws Error if used outside of PositionMarkerProvider
 */
export function usePositionMarker() {
  const context = useContext(PositionMarkerContext);
  if (context === undefined) {
    throw new Error("usePositionMarker must be used within a PositionMarkerProvider");
  }
  return context;
}